// Which client NTA report to print for one customer in one run, and the
// customer-facing wording for NMIs with no recommendation (client request #4,
// spec D-06). Pure: no React, no Supabase.
//
// C-01: nothing here may put a tariff code, rate or component on the report.
// Raw `reasons` strings carry tariff codes ("Tariff Change [SBTOU]: ..."), so
// they are only ever matched against patterns, never printed.

import { toNum, type NtaResultRow } from './nta';

// ── D-06 draft wording ─────────────────────────────────────────────────────
// Draft customer wording for the no-recommendation report and the mixed-client
// note. Choice Energy to confirm or edit; every customer-facing sentence the
// no-recommendation variant prints lives in this block.
export const NTA_NO_REC_WORDING = {
  title: 'Business Network Tariff Review for',
  intro: (customer: string) =>
    `Choice Energy has conducted a Network Tariff and Demand Analysis review for ${customer}`,
  introRest: 'to ensure the costs imposed by your electricity distributor(s) best reflects your usage profile.',
  allBest: 'Your site(s) are already on the best eligible network tariff, so no change is recommended at this time.',
  someNotAssessed:
    'No change is recommended at this time. Where a site could not be fully assessed, the reason is shown in the site summary.',
  allNotAssessed:
    'We were unable to complete the assessment for your site(s) this round. The reasons are shown in the site summary.',
  nextReview: 'We will review your network tariff again at the next six-monthly assessment.',
  circleMain: 'No change',
  circleSub: 'recommended',
  siteTableHeading: 'Site summary',
  siteTableColumns: ['NMI', 'Site address', 'Outcome'] as const,
  siteBest: 'Already on the best eligible network tariff. No change recommended.',
  reasonGeneric: 'We could not complete the assessment for this site. Your energy manager will follow up with you.',
  mixedNote: (nmis: string[], more: number) =>
    `No change is recommended for NMI ${nmis.join(', ')}${more > 0 ? ` and ${more} more` : ''}: ` +
    'already on the best eligible network tariff, or not able to be assessed this round.',
} as const;

/** Raw engine reason (pattern) → customer wording. First match per reason wins. D-06 draft wording. */
export const NTA_REASON_WORDING: ReadonlyArray<{ match: RegExp; text: string }> = [
  { match: /no network distributor/i, text: 'We could not confirm which electricity distributor supplies this site.' },
  { match: /no network tariff/i, text: 'We could not confirm the network tariff currently applied to this site.' },
  {
    match: /not fully mapped|could not be fully priced/i,
    text: "The distributor's published rates for your current network tariff could not be fully confirmed for this review.",
  },
  { match: /no interval data/i, text: 'Not enough interval meter data was available for the 12-month review period.' },
  { match: /missing input: annual import usage/i, text: 'Annual usage data for this site was incomplete.' },
  { match: /no available demand/i, text: 'Demand data for this site was not available.' },
];
// ── end D-06 draft wording ─────────────────────────────────────────────────

/** How many NMIs the mixed-client note names before "and N more". */
export const MIXED_NOTE_MAX_NMIS = 6;

export type NtaNmiOutcome = 'saving' | 'no_saving' | 'cannot_assess' | 'pending';
export type NtaReportVariant = 'recommendation' | 'no_recommendation' | 'pending' | 'empty';

export type NtaReportSite = {
  nmi: string;
  address: string;
  outcome: NtaNmiOutcome;
  /** Customer wording for the outcome — never a raw reason. */
  detail: string[];
};

export type NtaReportPlan = {
  variant: NtaReportVariant;
  /** Customer-level figure: sum of the saving NMIs. */
  total: number;
  sites: NtaReportSite[];
  savingNmis: string[];
  /** Sites with no change recommended (no saving or cannot assess). */
  noChangeSites: NtaReportSite[];
};

type ResultLike = Pick<NtaResultRow, 'nmi' | 'status' | 'savings' | 'reasons' | 'created_at'> & {
  site?: Pick<NonNullable<NtaResultRow['site']>, 'addr_street' | 'addr_city'> | null;
};

/**
 * One NMI's outcome. Unknown statuses (later lifecycle values such as
 * accepted/finalised) count as a saving when the result carries one.
 */
export function nmiOutcome(r: Pick<ResultLike, 'status' | 'savings'>): NtaNmiOutcome {
  const status = r.status as string;
  if (status === 'queued' || status === 'running') return 'pending';
  if (status === 'no_saving') return 'no_saving';
  if (status === 'cannot_assess' || status === 'failed') return 'cannot_assess';
  return (toNum(r.savings) ?? 0) > 0 ? 'saving' : 'no_saving';
}

/** A reason scoped to one scenario ("Baseline [X]: ..." / "Tariff Change [Y]: ...") explains a candidate, not the block. */
const SCENARIO_SCOPED = /^[^:]*\[[^\]]+\]\s*:/;

/** Customer wording for why an NMI could not be assessed; deduped; generic when nothing maps. */
export function customerReasons(reasons: string[] | null | undefined): string[] {
  const out: string[] = [];
  for (const raw of reasons ?? []) {
    if (SCENARIO_SCOPED.test(raw)) continue;
    const hit = NTA_REASON_WORDING.find((w) => w.match.test(raw));
    if (hit && !out.includes(hit.text)) out.push(hit.text);
  }
  return out.length ? out : [NTA_NO_REC_WORDING.reasonGeneric];
}

const r2 = (n: number) => Math.round(n * 100) / 100;

const addressOf = (r: ResultLike) =>
  [r.site?.addr_street, r.site?.addr_city].map((s) => (s ?? '').trim()).filter(Boolean).join(', ');

/** Latest result per NMI (a re-run can leave more than one row for an NMI in a run). */
function latestPerNmi<T extends ResultLike>(results: T[]): T[] {
  const by = new Map<string, T>();
  for (const r of results) {
    const prev = by.get(r.nmi);
    if (!prev || r.created_at > prev.created_at) by.set(r.nmi, r);
  }
  return [...by.values()].sort((a, b) => a.nmi.localeCompare(b.nmi));
}

/**
 * Which report to print:
 * - empty: no results for the customer in the run
 * - pending: any NMI still queued/running — wait before issuing
 * - recommendation: at least one NMI with a saving (others listed in the mixed note)
 * - no_recommendation: every NMI is no_saving / cannot_assess
 */
export function planClientReport(results: ResultLike[]): NtaReportPlan {
  const latest = latestPerNmi(results);
  const sites: NtaReportSite[] = latest.map((r) => {
    const outcome = nmiOutcome(r);
    const detail = outcome === 'no_saving' ? [NTA_NO_REC_WORDING.siteBest]
      : outcome === 'cannot_assess' ? customerReasons(r.reasons)
      : [];
    return { nmi: r.nmi, address: addressOf(r), outcome, detail };
  });
  const savingRows = latest.filter((r) => nmiOutcome(r) === 'saving');
  const total = r2(savingRows.reduce((s, r) => s + (toNum(r.savings) ?? 0), 0));
  const noChangeSites = sites.filter((s) => s.outcome === 'no_saving' || s.outcome === 'cannot_assess');
  const variant: NtaReportVariant = sites.length === 0 ? 'empty'
    : sites.some((s) => s.outcome === 'pending') ? 'pending'
    : savingRows.length > 0 ? 'recommendation'
    : 'no_recommendation';
  return { variant, total, sites, savingNmis: savingRows.map((r) => r.nmi), noChangeSites };
}

/** The no-recommendation summary sentence for these sites. */
export function noRecSummary(sites: NtaReportSite[]): string {
  const cannot = sites.filter((s) => s.outcome === 'cannot_assess').length;
  if (cannot === 0) return NTA_NO_REC_WORDING.allBest;
  return cannot === sites.length ? NTA_NO_REC_WORDING.allNotAssessed : NTA_NO_REC_WORDING.someNotAssessed;
}

/** The mixed-client note, or null when every NMI has a saving. */
export function mixedNote(noChangeSites: NtaReportSite[], max = MIXED_NOTE_MAX_NMIS): string | null {
  if (noChangeSites.length === 0) return null;
  const nmis = noChangeSites.map((s) => s.nmi);
  return NTA_NO_REC_WORDING.mixedNote(nmis.slice(0, max), Math.max(0, nmis.length - max));
}

/** Rows per printed site-summary page. */
export const SITE_ROWS_PER_PAGE = 22;

export function chunkSites<T>(rows: T[], size = SITE_ROWS_PER_PAGE): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < rows.length; i += size) out.push(rows.slice(i, i + size));
  return out;
}
